import {IAccessToken, ILoginForm, IRefreshToken, ISignupForm, ITokens,} from './auth.models';

import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {Observable, tap} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {isPlatformServer} from '@angular/common';
import {Router} from '@angular/router';
import {InterceptorSkipHeader} from '../../interceptors/token.interceptor';

@Injectable({providedIn: 'root'})
export class AuthService {
  private platform = inject(PLATFORM_ID);
  private httpClient = inject(HttpClient);
  private router = inject(Router);


  login(data: ILoginForm): Observable<ITokens> {
    return this.httpClient
      .post<ITokens>(`${environment.apiUrl}/companies/auth/signin`, data, {
        headers: new HttpHeaders({[InterceptorSkipHeader]: 'true'}),
      })
      .pipe(
        tap((response) => {
          this.setAccessToken(response.accessToken);
          this.setRefreshToken(response.refreshToken);
          this.router.navigate(['/app/users']);
        })
      );
  }

  signup(data: ISignupForm): Observable<ITokens> {
    return this.httpClient
      .post<ITokens>(`${environment.apiUrl}/companies/auth/signup`, data, {
        headers: new HttpHeaders({[InterceptorSkipHeader]: 'true'}),
      })
      .pipe(
        tap((response) => {
          this.setAccessToken(response.accessToken);
          this.setRefreshToken(response.refreshToken);
        })
      );
  }

  logout(): void {
    this.removeAccessToken();
    this.removeRefreshToken();
    this.router.navigate(['/app/login']);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  setAccessToken(token: IAccessToken): void {
    localStorage.setItem('access_token', JSON.stringify(token));
  }

  getAccessToken(): IAccessToken | null {
    if (isPlatformServer(this.platform)) {
      return null;
    }

    try {
      const accessToken = localStorage.getItem('access_token');

      if (accessToken) {
        return JSON.parse(accessToken);
      }
    } catch (_) {
      return null;
    }

    return null;
  }

  removeAccessToken(): void {
    localStorage.removeItem('access_token');
  }

  setRefreshToken(token: IRefreshToken): void {
    localStorage.setItem('refresh_token', JSON.stringify(token));
  }

  getRefreshToken(): IRefreshToken | null {
    if (isPlatformServer(this.platform)) {
      return null;
    }

    const refreshToken = localStorage.getItem('refresh_token');

    if (refreshToken) {
      return JSON.parse(refreshToken);
    }

    return null;
  }

  removeRefreshToken(): void {
    localStorage.removeItem('refresh_token');
  }

  refreshToken(): Observable<ITokens> {
    return this.httpClient
      .post<ITokens>(
        `${environment.apiUrl}/companies/auth/refresh`,
        {token: this.getRefreshToken()?.uid},
        {headers: new HttpHeaders({[InterceptorSkipHeader]: 'true'})}
      )
      .pipe(
        tap((response) => {
          this.setAccessToken(response.accessToken);
          this.setRefreshToken(response.refreshToken);
        })
      );
  }

  forgotPassword(email: string): Observable<void> {
    return this.httpClient.put<void>(
      `${environment.apiUrl}/companies/password/forgot`,
      null,
      {
        headers: new HttpHeaders({[InterceptorSkipHeader]: 'true'}),
        params: {
          email,
        },
      }
    );
  }
}
